import { useCallback } from "react";

import {
  decodeHtmlEntities,
  EMOJI_BY_TAG,
  EMPTY_VALUE_DISPLAY,
  IRichLogRecord,
  isEmojiTag,
  NO_VALUE_DISPLAY,
  RichLogTextStyle,
} from "@ryde/shared";

import {
  extractPriceRateSearchIdStrips,
  formatRichLogLinkAnchorText,
  isPriceRateSearchIdStripTag,
} from "../rich-logs.id-strips";

export const useGenerateLogHtml = (options?: { idOnlyLinks?: boolean }) => {
  const idOnlyLinks = options?.idOnlyLinks === true;

  const generateHtmlFromRichRecord = useCallback(
    (record: IRichLogRecord): string => {
      // Return "--" if message is absent
      if (!record.message) {
        return NO_VALUE_DISPLAY;
      }

      const { messageParams } = record;
      let result = record.message;

      // Regular expression to match placeholders like {{key}}
      const paramRegex = /\{\{([^}]+)\}\}/g;

      // Replace each placeholder with corresponding HTML
      result = result.replace(paramRegex, (match, paramKey) => {
        const key = paramKey.trim(); // Trim whitespace

        if (isEmojiTag(key)) {
          return EMOJI_BY_TAG[key].emoji;
        }

        if (isPriceRateSearchIdStripTag(key)) {
          return "";
        }

        // 1. Check in linkItems first
        if (messageParams?.linkItems?.[key]) {
          const linkItemGroup = messageParams.linkItems[key];
          const { entity, linkItems } = linkItemGroup;

          if (linkItems?.length) {
            return linkItems
              .map(
                item =>
                  `<a data-id="${item.id}" data-entity="${entity}">${formatRichLogLinkAnchorText(
                    {
                      entity,
                      item,
                      idOnly: idOnlyLinks,
                    },
                  )}</a>`,
              )
              .join(", ");
          }
        }

        // 2. Check in textInsertions
        if (messageParams?.textInsertions?.[key]) {
          const textInsertion = messageParams.textInsertions[key];
          let textHtml = textInsertion.text ?? NO_VALUE_DISPLAY;

          // Apply text style if specified
          if (textInsertion.textStyle) {
            switch (textInsertion.textStyle) {
              case RichLogTextStyle.Bold:
                textHtml = `<strong>${textHtml}</strong>`;
                break;
              case RichLogTextStyle.Italic:
                textHtml = `<em>${textHtml}</em>`;
                break;
              case RichLogTextStyle.Red:
                textHtml = `<span style="color: red;">${textHtml}</span>`;
                break;

              default:
                break;
            }
          }

          // Wrap with class if className is provided
          if (textInsertion.className) {
            textHtml = `<span class="${textInsertion.className}">${textHtml}</span>`;
          }

          return textHtml;
        }

        // If no match found, return "--" text
        return NO_VALUE_DISPLAY;
      });

      if (idOnlyLinks) {
        result = result.replace(
          /\((\d+) removed\)/g,
          "<strong>($1 removed)</strong>",
        );
      }

      return result;
    },
    [idOnlyLinks],
  );

  const generateTextForCsvExportDecoded = useCallback(
    (record: IRichLogRecord): string => {
      if (!record.message) {
        return EMPTY_VALUE_DISPLAY;
      }

      const { messageParams } = record;
      let result = record.message;

      const paramRegex = /\{\{([^}]+)\}\}/g;

      result = result.replace(paramRegex, (match, paramKey) => {
        const key = paramKey.trim();

        if (isEmojiTag(key)) {
          return EMOJI_BY_TAG[key].emoji;
        }

        if (isPriceRateSearchIdStripTag(key)) {
          return "";
        }

        if (messageParams?.linkItems?.[key]) {
          const { entity, linkItems } = messageParams.linkItems[key];

          if (linkItems?.length) {
            return linkItems
              .map(item =>
                formatRichLogLinkAnchorText({
                  entity,
                  item,
                  idOnly: idOnlyLinks,
                }),
              )
              .join(", ");
          }
        }

        if (messageParams?.textInsertions?.[key]) {
          const textInsertion = messageParams.textInsertions[key];
          return textInsertion.text ?? NO_VALUE_DISPLAY;
        }

        return match;
      });

      const strips = extractPriceRateSearchIdStrips(record);
      if (strips.length) {
        const stripText = strips
          .map(
            strip =>
              `${strip.kind}: ${strip.items.map(item => item.id).join(", ")}`,
          )
          .join("; ");
        result = `${result} ${stripText}`.trim();
      }

      return decodeHtmlEntities(result);
    },
    [idOnlyLinks],
  );

  return { generateHtmlFromRichRecord, generateTextForCsvExportDecoded };
};
